import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { Base, MagnifyingGlassIcon } from '@itsrever/design-system'
import { ShippingStatuses, stripBarcodeScannerPrefix } from '@/utils'
import { ReviewStatus } from '@/domain/review'
import {
  useLineItemReturnReasons,
  usePortalTranslate,
  useSelectedEcommerceIds
} from '@/hooks'
import { type ChangeEvent, useState } from 'react'
import { DateRangePicker } from '@/ui/shared/date-range'
import { FilterSelect } from '@/ui/shared/checklist'

const { Input } = Base

const shippingStatus = Object.values(ShippingStatuses).slice(1)

// What "Clear all" clears: everything but the tab, which return_status holds.
const FILTER_PARAMS = [
  'search',
  'from',
  'to',
  'line_item_return_reason',
  'last_known_shipping_status',
  'line_items.reviews.status'
]

const reviewStatus = [
  'APPROVED',
  'DECLINED',
  'MISSING'
] satisfies ReviewStatus[]

export const ItemsFilters = () => {
  const { t } = useTranslation()
  const { trans } = usePortalTranslate({})
  const { data: usedReturnReasons = [] } = useLineItemReturnReasons(
    useSelectedEcommerceIds()
  )
  // Reasons sharing a label (DEFECTIVE_MATERIAL and its _USED twin) are one option filtering both
  const reasonsByLabel = new Map<string, string[]>()
  for (const reason of usedReturnReasons) {
    const label = trans(`select_reason_${reason}`)
    reasonsByLabel.set(label, [...(reasonsByLabel.get(label) ?? []), reason])
  }
  const reasonOptions = [...reasonsByLabel].map(([label, reasons]) => ({
    label,
    value: reasons.join(',')
  }))

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const tabStatus = searchParams.get('return_status')
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const search = searchParams.get('search') ?? ''
  const [searchInputText, setSearchInputText] = useState(
    stripBarcodeScannerPrefix(search)
  )

  const resetPage = () => searchParams.delete('page')

  const setFilter = (param: string, values: string[]) => {
    resetPage()
    searchParams.delete(param)
    values.forEach((value) => searchParams.append(param, value))
    setSearchParams(searchParams)
  }

  const resetFilters = () => {
    setSearchInputText('')
    navigate(`${location.pathname}?return_status=${tabStatus ?? 'ALL_ITEMS'}`)
  }

  const displayClearFiltersButton = FILTER_PARAMS.some((param) =>
    searchParams.has(param)
  )

  return (
    <div className="flex justify-between gap-4 border-y border-neutral-200 pb-3 pt-4">
      <div className="w-[19rem]">
        <Input
          value={searchInputText}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = stripBarcodeScannerPrefix(e.target.value)
            setSearchInputText(value)

            if (value.length > 0) {
              resetPage()
              searchParams.set('search', value)
            } else {
              searchParams.delete('search')
            }
            setSearchParams(searchParams)
          }}
          type="search"
          placeholder={t('items.search_by')}
          prefix={<MagnifyingGlassIcon className="size-5" />}
          clear={searchInputText ? searchInputText.length > 0 : false}
          onClear={() => {
            setSearchInputText('')
            searchParams.delete('search')
            setSearchParams(searchParams)
          }}
        />
      </div>
      <div className="flex w-fit items-center justify-end gap-2">
        {displayClearFiltersButton && (
          <button
            className="cursor-pointer text-nowrap text-xs font-medium text-neutral-600"
            onClick={resetFilters}
          >
            {t('items.clear_all')}
          </button>
        )}
        <div className="w-60">
          <DateRangePicker
            selected={{
              from: from ? new Date(from) : undefined,
              to: to ? new Date(to) : undefined
            }}
            onSelect={(date) => {
              resetPage()
              searchParams.set('from', date?.from?.toISOString() ?? '')
              searchParams.set('to', date?.to?.toISOString() ?? '')
              setSearchParams(searchParams)
            }}
          />
        </div>
        <FilterSelect
          param="line_item_return_reason"
          label={t('items.return_reason')}
          options={reasonOptions}
          onChange={(values) => setFilter('line_item_return_reason', values)}
        />
        <FilterSelect
          param="last_known_shipping_status"
          label={t('items.shipping')}
          options={shippingStatus.map((status) => ({
            value: status,
            label: t(`shipping_status.${status}`)
          }))}
          onChange={(values) => setFilter('last_known_shipping_status', values)}
        />
        <FilterSelect
          param="line_items.reviews.status"
          label={t('items.review')}
          options={reviewStatus.map((status) => ({
            value: status,
            label: t(`line_item_review_status.${status}`)
          }))}
          onChange={(values) => setFilter('line_items.reviews.status', values)}
        />
      </div>
    </div>
  )
}
